import { type TeamEntry } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import {
  resolveParticipantLabel,
  type ParticipantLabelPlayer,
} from '@/app/[locale]/event/[eventId]/libs/participantLabel';

/**
 * One side of a scheduled match: the player's name parts (script-aware
 * formatting is applied inside `resolveParticipantLabel`, not upstream) plus
 * the team entry for that player in that division, when one exists.
 */
export interface ScheduleMatchSide {
  player?: ParticipantLabelPlayer | null;
  entry?: TeamEntry | null;
}

/**
 * Pure projection used by `eventModelV2.getPublicSchedule`: label = team name
 * → a set alias → the script-aware personal name (`resolveParticipantLabel`),
 * falling back to "TBD" when none of those resolve (no player joined, or a
 * player row with no name/alias at all); roster = the team entry's roster
 * verbatim (line breaks kept — the schedule detail sheet renders it
 * `whitespace-pre-line`), or null. Isolated here so it's unit-testable
 * without a Supabase mock.
 */
export function projectSchedulePlayers(
  sides: [ScheduleMatchSide, ScheduleMatchSide],
  isJapanese = false,
): {
  players: [string, string];
  rosters: [string | null, string | null];
} {
  const labelOf = (side: ScheduleMatchSide): string =>
    resolveParticipantLabel({ entry: side.entry, player: side.player, isJapanese }) || 'TBD';

  return {
    players: [labelOf(sides[0]), labelOf(sides[1])],
    rosters: [sides[0].entry?.roster ?? null, sides[1].entry?.roster ?? null],
  };
}
